import type { ProtocolIssue } from "./protocolGrouping";

export type ProtocolQuestionType = {
  section: "comparison" | "clarification";
  kind: "comparison" | "missing" | "note";
  label: string;
  action: string | null;
};

/** Classify the display, not the work quantity or the validity of a saved decision. */
export function protocolQuestionType(issue: ProtocolIssue): ProtocolQuestionType {
  const hasPair = issue.left != null && issue.right != null;
  const section = hasPair ? "comparison" : "clarification";
  const reason = issue.reason.trim();
  const historicalUnknown = issue.kind === "note" && /^Объём не установлен по исходным данным\.?$/u.test(reason);
  const missingValue = [issue.left, issue.right].some(side => side != null && side.value == null);

  if (historicalUnknown) {
    // This stored analysis note may be historical. It does not establish the
    // current work.quantity and must remain a separate issue with its own state.
    return { section, kind: "missing", label: "Недостаточно данных", action: "Уточнить основание для объёма" };
  }
  if (issue.kind === "incomplete" || missingValue) {
    const unknownWorkQuantity = issue.kind === "incomplete" && /^Объём не установлен\.?$/u.test(reason);
    return {
      section, kind: "missing", label: "Недостаточно данных",
      action: unknownWorkQuantity ? "Установить объём работы" : "Уточнить недостающие данные",
    };
  }
  if (hasPair && ["quantity", "units", "zero_base"].includes(issue.kind)) {
    return { section, kind: "comparison", label: "Сравнение источников", action: null };
  }

  if (issue.kind === "note") {
    // Quote only numbers explicitly present in this complete, known sentence.
    // They are not parsed as measurements, calculated, or accepted as a correction.
    const countNote = /^Количество не определено: ([0-9]+) в ячейке, ([0-9]+) в правке; не суммировать\.?$/u.exec(reason);
    if (countNote) {
      return {
        section, kind: "note", label: "Пояснение к количеству",
        action: `Уточнить, заменяет ли правка ${countNote[1]} на ${countNote[2]}`,
      };
    }
    if (/^Подтвердить правку материала, крепление, обмер в 100 м[2²], техчасть и редакцию\.?$/u.test(reason)) {
      return {
        section, kind: "note", label: "Уточнение по работе",
        action: "Проверить материал, крепление и норму монтажа",
      };
    }
  }
  const label = issue.work_id != null ? "Уточнение по работе"
    : issue.document_id ? "Уточнение по документу" : "Общее уточнение";
  return { section, kind: "note", label, action: "Проверить замечание по источнику" };
}
